<template>
  <div @dragover="dragoverEvent" id="inside-folder-body" :class="scrollDisabled ? 'overflow-hidden' : 'overflow-auto'" class="w-full relative h-screen">
    <Header :title="folder.name + ' '" :count="count ? count : 0" @createFolder="createFolder" @displayDownloadSection="displayDownloadSection" />
    <div class="w-full px-6 md:h-20 border-b">
      <div class="h-full w-full flex justify-start md:justify-center items-center mx-auto relative">
        <div class="md:absolute left-0 inset-y-0 flex flex-col md:flex-row items-start md:items-center">
          <MyMediaSearchInput @search="addFilter('search', $event)" input-classes="bg-smoke" class="mr-2 md:mr-5 mt-3 mb-2 md:mt-0 md:mb-0"/>
          <MyMediaFilters @change="runSearch" :shownFilters="shownFilters" :activeFilters="activeFilters" />
        </div>
        <div class="absolute right-0 md:inset-y-0 top-3 md:top-0 flex items-center text-xs">
          <button @click="changeViewType('Grid')" class="btn p-2 flex items-center justify-center rounded-full text-payne hover:opacity-60 hover:text-opacity-60 transition">
            Grid
            <GridIcon class="ml-1"/>
          </button>
          <p>/</p>
          <button @click="changeViewType('List')" class="btn p-2 flex items-center justify-center rounded-full text-payne hover:opacity-60 hover:text-opacity-60 transition">
            List
            <ListIcon class="ml-1"/>
          </button>
        </div>
      </div>
      <div class="flex flex-row items-center justify-start mt-5">
        <div class="flex items-center cursor-pointer" @click="goBack">
          <Arrow class="transform rotate-90 mr-2" />
          <p class="text-xs text-yank">Back</p>
        </div>
      </div>
      <Transition name="fade">
      <div v-if="showAsGrid" :class="showDetails ? 'w-2/3 2xl:w-9/12 grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6' : 'w-full grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'" class="p-5 grid gap-5 transition-all ease-out duration-200">
          <Folder v-for="(item) in folders" :key="item.id" :folder="item" @open="goToFolder" @openRenameModal="openManageFolderModal" @openDeleteModal="openDeleteFolderModal"/>
          <ChurchItemComponent v-for="(item) in items" :withFavorite="true" @rename="openRenameItemrModal(item)" @click="selectMedia(item)" :key="item.id" :item="item" @deleteItem="openDeleteItemModal"/>
      </div>
      </Transition>
      <Transition name="opacity">
        <div v-if="!showAsGrid" class="mt-5">
          <MediaTable>
            <template slot="row" class="outer">
              <MediaRow v-for="(item) in folders" :key="item.id" :item="item" @open="goToFolder" :isFolder="true" @openDeleteModal="openDeleteFolderModal"/>
              <MediaRow v-for="(item) in items" :key="item.id" :item="item" @click="selectMedia(item)" @deleteItem="openDeleteItemModal" @openMoveModal="openMoveModal" @openDetails="selectMedia(item)"/>
            </template>
          </MediaTable>
        </div>
      </Transition>
      <Transition name="details">
        <MediaDetails @close="showDetails=false" @deleteItem="openDeleteItemModal" v-if="showDetails" :selectedMedia="selectedMedia" />
      </Transition>
      <div class="w-full flex items-center justify-center" v-if="folders && folders.length === 0 && items && items.length === 0"> 
        <span class="my-8 text-sm font-medium text-payneFire">No items to display</span>
      </div>
    </div>
    <portal to="destination"> 
      <div v-if="manageFolderModalOpen" class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full">
        <RenameModal v-on:closeModal="closeManageFolderModal" :item="folderToManage" @rename="renameFolder"/>
      </div>
      <div v-if="renameItemModalOpen" class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full">
        <RenameModal v-on:closeModal="closeItemRenameModal" :item="itemToManage" @rename="renameItem"/>
      </div>
      <div v-if="confirmDeleteModalOpen" class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full">
        <ConfirmDeleteModal v-on:closeModal="closeConfirmDeleteModal" :folder="folderToManage" @delete="deleteFolder"/>
      </div>
    </portal>
    <div v-show="showUploadSection || (currentUploadingItems && currentUploadingItems.length > 0) || currentUploadingErrors > 0" class="absolute inset-0 flex items-end h-screen justify-center p-10">
      <div v-on-clickaway="forceCloseUploadZone" v-show="showUploadSection" class="cursor-pointer absolute z-40 bottom-20 md:bottom-4 right-5 left-5">
        <UploadSection :folderId="folder.id" @close="closeUploadSection" />
      </div>
      <div v-if="(currentUploadingItems && currentUploadingItems.length > 0) || currentUploadingErrors > 0" class="cursor-pointer absolute z-40 bottom-20 md:bottom-4 right-5 left-5">
        <UploadResults :currentUploading="currentUploadingItems" :uploadErrors="uploadErrors" :uploadSuccess="uploadSuccess" @changeCurrentUploadingErrors="changeCurrentUploadingErrors" @removeFromCurrent="removeFromCurrent" />
      </div>
    </div>
    <portal to="destination"> 
        <div v-show="confirmDeleteItemModalOpen" class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full">
        <ConfirmDeleteItemModal v-on:closeModal="confirmDeleteItemModalOpen = false" :item="itemToManage" @delete="deleteItem"/>
        </div>
    </portal>
    <portal to="destination"> 
        <div v-if="showMoveModal" class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full">
          <MoveItemModal v-on="$listeners" @closeModal="showMoveModal = false" :item="itemToManage"/>
        </div>
    </portal>
  </div>
</template>

<script>
import AppLayout from '@/layout/app-layout'
import MyMediaSearchInput from '@/components/shared/MyMediaSearchInput'
import Arrow from '@/components/svg/Arrow'
import Folder from '@/components/shared/Folder'
import ChurchItemComponent from '@/components/my-media/ChurchItemComponent'
import MediaDetails from '@/components/my-media/MediaDetails'
import ConfirmDeleteModal from '@/components/my-media/ConfirmDeleteModal'
import MyMediaFilters from '@/components/my-media/MyMediaFilters.vue'
import GridIcon from '@/components/svg/GridIcon'
import ListIcon from '@/components/svg/ListIcon'
import MediaTable from '@/components/my-media/Table'
import MediaRow from '@/components/my-media/Table/partials/Row'
import UploadSection from '@/components/my-media/Upload/UploadSection.vue'
import UploadResults from '@/components/my-media/Upload/UploadResults.vue'
import ConfirmDeleteItemModal from '@/components/my-media/ConfirmDeleteItemModal'
import Header from '@/components/my-media/Header'
import RenameModal from '@/components/my-media/RenameModal.vue'
import MoveItemModal from '@/components/my-media/MoveItemModal.vue'
import { mixin as clickaway } from 'vue-clickaway';

export default {
  name: 'MyMedia',
  layout: AppLayout,

  mixins: [ clickaway ],

  data() {
    return {
      showAsGrid: this.viewType !== 'List',
      showDetails:false,
      selectedMedia:null,
      activeFilters: this.filters || {},
      manageFolderModalOpen: false,
      renameItemModalOpen: false,
      folderToManage: null,
      itemToManage: null,
      confirmDeleteModalOpen: false,
      showUploadSection: false,
      uploadErrors: null,
      uploadSuccess: null,
      currentUploadingItems: [],
      currentUploadingErrors: 0,
      confirmDeleteItemModalOpen: false,
      itemToManage: null,
      scrollDisabled: false,
      dragoverTime: null,
      showMoveModal: false
    }
  },

  components: {
      UploadSection,
      UploadResults,
      MyMediaSearchInput,
      Arrow,
      Folder,
      ChurchItemComponent,
      MediaDetails,
      MyMediaFilters,
      GridIcon,
      ListIcon,
      MediaTable,
      MediaRow,
      MediaDetails,
      ConfirmDeleteModal,
      ConfirmDeleteItemModal,
      Header,
      RenameModal,
      MoveItemModal
  },

  props:{
      items: Array,
      folders: Array,
      folder: Object,
      filters: Object,
      flash: Object,
      count: Number,
      viewType: String,
  },

  computed:{
      shownFilters() {
        let shownFilters = {...this.activeFilters};
        if(shownFilters.account) {
          let pagesNames = this.getPagesNames(shownFilters.account);
          shownFilters.account = pagesNames;
        }

        return shownFilters;
      },

      parsedFilters() {
        const filters = {};

        Object.keys(this.activeFilters).forEach(key => {
          if (this.activeFilters[key] === null) {
            delete this.activeFilters[key];
            return;
          }

          let keyName = `filter[${key}]`;
          filters[keyName] = this.activeFilters[key];
        });

        filters['viewType'] = this.showAsGrid ? 'Grid' : 'List';

        return filters;
      },
  },

  methods:{
      dragoverEvent() {
        this.showUploadSection = true;
        this.disableScroll();
        clearTimeout(this.dragoverTime);
        this.dragoverTime = setTimeout(() => {
          this.forceCloseUploadZone();
        }, 1500) 
      },

      addFilter(type, value) {
        this.$set(this.activeFilters, type, value);
        this.runSearch();
      },

      disableScroll() {
          this.scrollDisabled = true;
      },

      enableScroll() {
          this.scrollDisabled = false;
      },

      openManageFolderModal(folder){
          this.folderToManage = folder;
          this.manageFolderModalOpen = true;
      },

      openRenameItemrModal(item){
          this.itemToManage = item;
          this.renameItemModalOpen = true;
      },

      openMoveModal(item) {
        this.showMoveModal = true; 
        this.itemToManage = item;
        if(this.includesVideo) {
          this.$refs.video.pause();
        }
      },

      removeFromCurrent(requestId) {
        this.currentUploadingItems = this.currentUploadingItems.filter(item => item.request_id != requestId);
        if(this.currentUploadingItems.length === 0) {
          this.enableScroll();
        }
      },

      changeCurrentUploadingErrors(value) {
        this.currentUploadingErrors = value;
      },

      closeUploadSection(files) {
        this.currentUploadingItems = [...files]; 
        this.showUploadSection = false;
      },

      forceCloseUploadZone() {
        this.enableScroll();
        this.showUploadSection = false;
      },

      displayDownloadSection() {
        setTimeout(() => {
          if(this.currentUploadingItems.length === 0 && this.currentUploadingErrors === 0) {
            this.showUploadSection = true;
            this.disableScroll();
          } else {
            this.$toast.error('Please wait until the upload finishes');
            this.flash.error = '';
          }
        }, 50);
      },

      closeManageFolderModal(){
          this.manageFolderModalOpen = false;
      },

      closeItemRenameModal(){
          this.renameItemModalOpen = false;
      },

      renameFolder(folderName, folder){
          try{
              this.$inertia.put(this.$route('church-media.folders.updateName',folder.id), {
                name: folderName,
                redirectRoute: 'my-media.show',
                currentFolder: this.folder.id
              },
              {
                preserveScroll: true
              }
              );   
          }catch (ex) {
            this.flash.error = 'Only folders owners can rename them';
          }
      },

      renameItem(name, item){
          try{
              this.$inertia.put(this.$route('church-media.items.update',item.id), {
                name: name,
              },
              {
                preserveScroll: true
              }
              );   
          }catch (ex) {
            this.flash.error = 'Only folders owners can rename them';
          }
      },
      selectMedia(selectedItem){
      this.showDetails = true
      this.selectedMedia = selectedItem
    },
    closeConfirmDeleteModal(){
        this.confirmDeleteModalOpen = false;
    },

    openDeleteFolderModal(folder){
        this.folderToManage = folder;
        this.confirmDeleteModalOpen = true;
    },

    deleteFolder(folder){
          try{
            this.$inertia.delete(this.$route('church-media.folders.destroy',folder), {preserveScroll: true});   
          }catch (ex) {
            this.flash.error = 'Only folders owners can delete them';
          }
          
      },

    runSearch() {
      this.$inertia.get(this.$route('my-media.show', this.folder.id), this.parsedFilters, {
        onStart: (visit) => this.working = true,
        onFinish: (visit) => this.working = false,
        preserveScroll: true,
      })
    },
    
    changeViewType(typeView){
      typeView === 'Grid' ? this.showAsGrid=true : this.showAsGrid=false
    },

    goToFolder(id) {
      this.$inertia.get(this.$route('my-media.show', id), this.parsedFilters, {
        onStart: (visit) => this.working = true,
        onFinish: (visit) => this.working = false,
        preserveScroll: true,
      })
    },

    goBack() {
      if(this.folder.folder_id !== null) {
        return this.goToFolder(this.folder.folder_id);
      } 

      this.$inertia.get(this.$route('my-media'), this.parsedFilters, {
        onStart: (visit) => this.working = true,
        onFinish: (visit) => this.working = false,
        preserveScroll: true,
      })
    },

    createFolder(folderName) {
      try{
        const route = this.$route('church-media.folders.store');
        this.$inertia.post(route, 
          {
            name: folderName,
            folder_id: this.folder.id,
          },
          {
            preserveScroll: true
          }
        );   
      }catch (ex) {
        this.flash.error = 'There was a problem creating the folder, please try again later';
      }
    },

    openDeleteItemModal(item){
        this.itemToManage = item,
        this.confirmDeleteItemModalOpen = true;
    },
    
    deleteItem(item){
      try{
        this.$inertia.delete(this.$route('church-media.items.destroy', item), {preserveScroll:true});   
      }catch (ex) {
        this.flash.error = 'Only folders owners can delete them';
      }
    },

    deleteItemFromDetails() {
      this.deleteItem(this.selectedMedia)
      this.showDetails = false; 
    }
  },

  watch: {
    flash: {
      handler(flash) {
        if (flash.success && flash.success !== '') {
          this.$toast.success(flash.success);
        }
        if (flash.error && flash.error !== '') {
          this.$toast.error(flash.error);
          this.flash.error = '';
        }
        if (flash.uploadError) {
          this.uploadErrors = flash.uploadError;
        }
        if (flash.uploadSuccess) {
          this.uploadSuccess = flash.uploadSuccess;
        }
      },
      deep: true,
    },
    
  }
}
</script>
<style>
.fade-enter-active {
  animation: fade-in 0.5s;
}
.fade-leave-active {
  animation: fade-in 0.5s reverse;
}
.opacity-enter-active {
  animation: opacity-in 1s;
}
.opacity-leave-active {
  animation: opacity-in 0.4s reverse;
}
.details-enter-active {
  animation: details-in 0.5s;
}
.details-leave-active {
  animation: details-in 0.1s reverse;
}
@keyframes fade-in {
  0% {
    transform: translateX(40px);
    opacity: 0;
  }
  30%{
      opacity: 0;
  }
  100% {
   transform: translateX(0px);
   opacity: 1;
  }
}
@keyframes opacity-in {
  0% {
    transform: translateX(-40px);
    opacity: 0;
  }
  70%{
    transform: translateX(-40px);
      opacity: 0;
  }
  100% {
    transform: translateX(0px);
   opacity: 1;
  }
}
@keyframes details-in {
  0% {
    transform: translateX(40px);
    opacity: 0;
  }
  100% {
    transform: translateX(0px);
   opacity: 1;
  }
}
</style>
